import {appConfig, gaConfig} from '@stencil/hotels-config';

// SELECTORS
import {
  multiRoomFlowStatusSelector,
  isEntirePropertySelector,
  roomsDataSelector,
  amenitiesSelector as hotelsAmenitiesSelector,
  similarPropertiesSelector,
  hotelStaticHouseRulesSelector,
  reviewSummaryGISelector,
  hotelDetailsSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import debounce from '@stencil/hotels-utils/src/debounce';
import getDetailsActiveScrollStatus from '@stencil/hotels-utils/src/get-details-active-scroll-status';
import {
  detailsNavigationIds,
  detailsNavigationIdsAndValues,
  getDetailsNavigationIdsAndValuesObjectById,
} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';

// CONFIG
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';
import React, {useEffect, useRef, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
// HOOKS
import {useHistory, useLocation, useParams} from 'react-router-dom';
// STYLES
import * as S from './Navigation.styles';
import getVerticalAscendingOrderOfElements from '@stencil/hotels-utils/src/get-vertical-ascending-order-of-elements';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import {
  getDetailSeoPageUrl,
  getUnifPageName,
  seoNavigationTabs,
} from '@stencil/hotels-utils/src/seo-utils';
import {ugcSummarySelector} from '@stencil/hotels-cg-reducer/src/ugc/selectors';

const {detailsNavigationElementId, onLoadScrollToElementTimeoutDetails} = appConfig;

const Item: React.FC<{children: React.ReactNode; active?: boolean}> = ({children, active}) => (
  <li>
    <S.TextWrapperSpan {...(active ? {className: 'active'} : {})}>{children}</S.TextWrapperSpan>
  </li>
);

const Navigation = ({isSeo = false}) => {
  const [activeMenuItemId, setActiveMenuItemId] = useState('');
  const [hideNavigation, setHideNavigation] = useState(false);
  const [navSectionOrderedIds, setNavSectionOrderedIds] = useState([]);

  const navigationRef = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const {hash, pathname, search} = location;

  const pageNameSlug = getUnifPageName(search, params);
  const amenitiesList = useSelector(hotelsAmenitiesSelector);
  const roomsDataFromCg = useSelector(roomsDataSelector);
  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const snapData = useSelector(reviewSummaryGISelector);
  const similarHotelsData = useSelector(similarPropertiesSelector);
  const houseRules = useSelector(hotelStaticHouseRulesSelector);
  const hotelDetails = useSelector(hotelDetailsSelector);
  const ugcSummary = useSelector(ugcSummarySelector);

  const thereAreSimilarHotels = !!similarHotelsData?.slice(1).length;
  const thereAreRooms = multiRoomFlowStatus?.hasRecommendedCombos ? true : !!roomsDataFromCg.length;
  const {extDataExists, giDataExists, taDataExists, expDataExists} = snapData || {};

  useEffect(() => {
    // set active menu item from url hash, which helps with styles
    if (!hash) return;

    /**
     * If there's a hash on details page, that means
     * we intend to be scrolled down to a particular element.
     * In order to make the user see that they are being scrolled down
     * we wait for 'onLoadScrollToElementTimeoutDetails' amount of timeout before scrolling.
     * If we do not do this, then the scroll starts in a flash (without the user being
     * able to see where the scrolling started from).
     *
     * TL;DR
     *
     * This time out is for user interaction and perceivability enhancement
     */

    const allDataForPagePresent = !!amenitiesList.length && !!roomsDataFromCg.length;
    if (!document.querySelector(location.hash) || !allDataForPagePresent) return;
    scrollToEl(location.hash);
    setActiveMenuItemId(location.hash);
  }, [amenitiesList, roomsDataFromCg, similarHotelsData, houseRules, hash]);

  // https://css-tricks.com/sticky-smooth-active-nav/
  useEffect(() => {
    const allNavlinks = Array.from<HTMLAnchorElement>(
      document.querySelectorAll('#details-navigation a'),
    );

    // being super safe
    if (!allNavlinks.length) {
      return;
    }

    function updateActiveNavLink() {
      allNavlinks.forEach(link => {
        let section: HTMLElement = document.querySelector(link.hash);

        if (!section) {
          return;
        }
      });

      hideNavigationWhenFAQActive();
    }

    function hideNavigationWhenFAQActive() {
      let faqsSection = document.getElementById(detailsNavigationIds.faqs) as HTMLElement;
      if (!faqsSection) return;

      if (getDetailsActiveScrollStatus(faqsSection)) {
        setHideNavigation(true);
      } else {
        setHideNavigation(false);
      }
    }

    const debouncedFn = debounce(updateActiveNavLink, 150);

    window.addEventListener('scroll', updateActiveNavLink);

    return () => {
      window.addEventListener('remove', updateActiveNavLink);
      debouncedFn.cancel();
    };
  }, [amenitiesList, roomsDataFromCg, similarHotelsData, houseRules]);

  useEffect(() => {
    const orderIds = getVerticalAscendingOrderOfElements(
      Array.from(
        Object.values(detailsNavigationIdsAndValues).map(e => document.querySelector(e.id)),
      ),
    ).map(el => el.id);
    setNavSectionOrderedIds(orderIds);
  }, [amenitiesList, roomsDataFromCg, similarHotelsData, houseRules, thereAreRooms]);

  if (hideNavigation) {
    return null;
  }

  if (isSeo) {
    const tabsData = seoNavigationTabs.map(tab => {
      const pageUrl = getDetailSeoPageUrl(tab.id, params);

      return {
        ...tab,
        pageUrl,
        isActive: pageNameSlug === tab.id,
      };
    });
    return (
      <S.NavigationStyleContainer isSeo ref={navigationRef}>
        <S.StyledWrapper>
          <S.MenuList>
            {tabsData.map(tab => (
              <Item active={tab.isActive} key={tab.id}>
                {tab.isActive ? (
                  <S.ItemLinkTo active={tab.isActive}>{tab.name}</S.ItemLinkTo>
                ) : (
                  <S.ItemLinkTo href={tab.pageUrl} active={true}>
                    {tab.name}
                  </S.ItemLinkTo>
                )}
              </Item>
            ))}
          </S.MenuList>
        </S.StyledWrapper>
      </S.NavigationStyleContainer>
    );
  }

  return (
    <S.NavigationStyleContainer ref={navigationRef} id={detailsNavigationElementId}>
      <S.StyledWrapper>
        <S.MenuList>
          {/* sync this up with current active and scroll with current same page link urls */}

          {navSectionOrderedIds.map(_id => {
            const id = `#${_id}`;

            // only used for mweb, hence return null
            if (id === detailsNavigationIdsAndValues.overview.id) {
              return null;
            }
            // check if there's data to show for the navigation
            if (id === detailsNavigationIdsAndValues.amenities.id && !amenitiesList.length) {
              return null;
            }

            if (
              (id === detailsNavigationIdsAndValues.rooms.id ||
                id === detailsNavigationIdsAndValues.stays.id) &&
              !thereAreRooms
            ) {
              return null;
            }

            if (
              id === detailsNavigationIdsAndValues.guestReviews.id &&
              !(
                ugcSummary?.availableOTAs?.length ||
                giDataExists ||
                taDataExists ||
                extDataExists ||
                expDataExists
              )
            ) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.similarHotels.id && !thereAreSimilarHotels) {
              return null;
            }

            // if (id === detailsNavigationIdsAndValues.hotelPolicies.id && !houseRules?.length) {
            //   return null;
            // }

            return (
              <Item key={id} active={!!(activeMenuItemId && activeMenuItemId === id)}>
                <S.ItemLinkTo
                  href={id}
                  active={!!(activeMenuItemId && activeMenuItemId === id)}
                  data-testid={`navigation-header-cta-${id}`}
                  /**
                   * NOTE: not extracting this function in a separate function
                   * because it's a small function definition and is beneficial
                   * to understand what is happening here itself. And also because
                   * https://cdb.reacttraining.com/react-inline-functions-and-performance-bdff784f5578?gi=970e2972fe2e
                   */
                  onClick={(e: Event) => {
                    /**
                     * because we want to control scroll behavior
                     * we prevent a tag to 'snapping' to the desired element
                     * and instead manually push the history to (location + hash)
                     */
                    e.preventDefault();
                    console.log('detailsClick:', detailsClick);
                    dispatch(
                      omnitureThunk(
                        omnitureConfig.pages.DetailsUnif,
                        omnitureConfig.events.ctaClick,
                        {
                          cta: {
                            name: `${DetailsPageComponents.NavigationMenu}_${detailsClick.nav[id]}`,
                            type: omnitureConfig.ctaType.Button,
                            componentName: DetailsPageComponents.NavigationMenu,
                            destination: '',
                          },
                        },
                      ),
                    );

                    // current location + hash
                    history.push(pathname + search + id);

                    // set active menu item which helps with styles
                    // setActiveMenuItemId(id);

                    // scroll to desired element if it's there
                    scrollToEl(id);
                  }}
                >
                  {getDetailsNavigationIdsAndValuesObjectById(id) &&
                    getDetailsNavigationIdsAndValuesObjectById(id).value}
                </S.ItemLinkTo>
              </Item>
            );
          })}
        </S.MenuList>
      </S.StyledWrapper>
    </S.NavigationStyleContainer>
  );
};

export default Navigation;
